import { useContext } from 'react';
import {
  DetailContext,
  DetailHandle,
  UserContext,
  abortableFetch,
  useEventCallback,
} from '@eas/common-web';
import { InternalDocument, Me, OutgoingDocument, UserRole } from '@models';
import {
  Agenda,
  DecisionState,
  EsFileState,
  EvidenceApiUrl,
  Role,
} from '@enums';

type DecisionDocument = InternalDocument | OutgoingDocument;

function isCurrentUser(user: Me | null | undefined, userRole?: UserRole) {
  const userId = user?.id;
  const substitutedUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;
  const assignedUserId = userRole?.user?.id;

  return (
    !!assignedUserId &&
    (assignedUserId === userId || assignedUserId === substitutedUserId)
  );
}

export function useCancelDecisionDialog() {
  const { source } = useContext<DetailHandle<DecisionDocument>>(DetailContext);
  const { user } = useContext<UserContext<Me>>(UserContext);

  const isSuperAdmin = (user?.userRoles ?? []).some(
    ({ role }) => role === Role.SUPERADMIN
  );
  const hasRole = (role: Role) =>
    (user?.userRoles ?? []).some((userRole) => userRole.role === role);
  const hasSecretaryRole = hasRole(Role.SECRETARY);
  const hasIndexRole = hasRole(Role.INDEX);
  const isCurrentSecretary = isCurrentUser(
    user,
    source.data?.esFile?.secretaryUserRole
  );
  const isCurrentIndexUser = isCurrentUser(
    user,
    source.data?.esFile?.indexUserRole
  );

  const callApi = useEventCallback(() =>
    abortableFetch(
      `${EvidenceApiUrl.DOCUMENTS}/${source.data?.id}/decision/cancel`,
      {
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        method: 'POST',
      }
    )
  );

  const showInCourtFile =
    source.data?.esFile?.agenda === Agenda.COURT &&
    source.data.esFile.state &&
    [EsFileState.JUDGE_OFFICE, EsFileState.REGISTER].includes(
      source.data.esFile.state
    ) &&
    (isSuperAdmin || (hasSecretaryRole && isCurrentSecretary));

  const showInPlenaryOpinionFile =
    source.data?.esFile?.agenda === Agenda.PLENARY_OPINION &&
    source.data.esFile.state === EsFileState.PROPOSED &&
    (isSuperAdmin || (hasIndexRole && isCurrentIndexUser));

  const showButton =
    source.data?.id &&
    source.data.decision === true &&
    !!source.data.linkedDecision &&
    source.data.linkedDecision.state !== DecisionState.CANCELED &&
    (showInCourtFile || showInPlenaryOpinionFile);

  return {
    callApi,
    showButton,
  };
}
